/**
 * `SortableHeader` (DNKADM-0430): the one sortable column header for every list.
 *
 * A `TableCell` holding an MUI `TableSortLabel`: clicking it calls `onSort(col)`, the
 * active column shows the direction arrow, and the cell carries `aria-sort` (through
 * `sortDirection`) so assistive tech hears the current order. The click rule itself
 * (same column flips, new column starts ascending) is `toggleSort` in `./listSort`, so
 * the screen owns the state and can keep it in the URL.
 *
 * `sort` may be `null` for a list whose default is "server order" (no column active).
 * Columns that are not sortable stay plain `TableCell`s; do not render this for them.
 */
import { TableCell, TableSortLabel, type TableCellProps } from '@mui/material';
import type { SortState } from './listSort';

export interface SortableHeaderProps<K extends string> {
  /** The visible column label. */
  label: string;
  /** This column's sort key. */
  col: K;
  /** The active sort, or `null` when no column is sorted. */
  sort: SortState<K> | null;
  /** Called with this column's key on click. */
  onSort: (col: K) => void;
  align?: TableCellProps['align'];
  disabled?: boolean;
}

export function SortableHeader<K extends string>({
  label,
  col,
  sort,
  onSort,
  align,
  disabled = false,
}: SortableHeaderProps<K>) {
  const active = sort !== null && sort.key === col;
  return (
    <TableCell align={align} sortDirection={active ? sort.dir : false}>
      <TableSortLabel
        active={active}
        direction={active ? sort.dir : 'asc'}
        onClick={() => onSort(col)}
        disabled={disabled}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );
}
